import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MessageSquare, Users } from 'lucide-react';
import CampaignStatusBadge from './CampaignStatusBadge';

const CampaignListTable = ({ campaigns }) => {
  const navigate = useNavigate();

  const getChannelIcon = (channel) => {
    switch (channel?.toLowerCase()) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'sms':
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };

  const getChannelLabel = (channel) => {
    switch (channel?.toLowerCase()) {
      case 'email':
        return 'Email';
      case 'sms':
        return 'SMS';
      default:
        return channel || '—';
    }
  };

  const getProgress = (campaign) => {
    if (campaign.status === 'draft') return 'Not started';
    if (campaign.status === 'completed') return 'Completed';
    if (campaign.status === 'paused') return 'Paused';
    if (campaign.type === 'drip' && campaign.total_steps > 0) {
      return `Step ${(campaign.current_step || 0) + 1} of ${campaign.total_steps}`;
    }
    return 'In progress';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatType = (type) => {
    if (!type) return '—';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Campaign
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Channel
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Patients
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Progress
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {campaigns.map((campaign, index) => (
            <tr
              key={campaign.id || campaign.campaign_id || index}
              onClick={() => navigate(`/campaigns/${campaign.id || campaign.campaign_id}`)}
              className="hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <td className="px-6 py-4">
                <div>
                  <p className="text-sm font-medium text-slate-900">{campaign.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {formatType(campaign.type)} · Created {formatDate(campaign.created_at)}
                  </p>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2 text-slate-600">
                  {getChannelIcon(campaign.channel)}
                  <span className="text-sm">{getChannelLabel(campaign.channel)}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1.5 text-slate-600">
                  <Users className="h-4 w-4 text-slate-400" />
                  <span className="text-sm">{campaign.patientCount ?? 0}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="text-sm text-slate-600">{getProgress(campaign)}</span>
              </td>
              <td className="px-6 py-4">
                <CampaignStatusBadge status={campaign.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CampaignListTable;
